import { P } from '@piccolohealth/util';
import React from 'react';
import { ChakraV3, Select, type SelectOption } from '..';

export interface PaginationOptions {
  total: number;
  currentPage: number;
  pageSize: number;
  pageSizeOptions: number[];
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  nextPage: () => void;
  previousPage: () => void;
  showTotal: (total: number, range: [number, number]) => string;
  onPageSizeChange: (pageSize: number) => void;
}

export interface TimestampPaginationOptions {
  total?: number;
  pageSize: number;
  pageSizeOptions: number[];
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  nextPage: () => void;
  previousPage: () => void;
  showTotal?: (total: number) => string;
  onPageSizeChange: (pageSize: number) => void;
}

export const Pagination = (props: PaginationOptions | TimestampPaginationOptions) => {
  const total = React.useMemo(() => {
    if (!props.showTotal || !props.total) {
      return null;
    }

    if ('currentPage' in props) {
      return props.showTotal(props.total, [
        props.currentPage * props.pageSize + 1,
        Math.min((props.currentPage + 1) * props.pageSize, props.total),
      ]);
    }

    return props.showTotal(props.total);
  }, [props]);

  const options: SelectOption<number>[] = React.useMemo(() => {
    return props.pageSizeOptions.map((p) => ({
      label: `${p} / page`,
      value: p.toString(),
      raw: p,
    }));
  }, [props.pageSizeOptions]);

  const selectedValue: SelectOption<number> = {
    value: props.pageSize.toString(),
    label: `${props.pageSize} / page`,
    raw: props.pageSize,
  };

  return (
    <ChakraV3.HStack alignItems='center'>
      <ChakraV3.Text data-pw='paginationTotalText' color='secondary'>
        {total}
      </ChakraV3.Text>

      <ChakraV3.HStack>
        <ChakraV3.Button
          size='xs'
          variant='subtle'
          disabled={!props.hasPreviousPage}
          onClick={() => props.previousPage()}
        >
          Prev
        </ChakraV3.Button>
        <ChakraV3.Button
          variant='subtle'
          size='xs'
          disabled={!props.hasNextPage}
          onClick={() => props.nextPage()}
        >
          Next
        </ChakraV3.Button>
        <Select
          size='sm'
          value={selectedValue}
          onChange={(e) => props.onPageSizeChange(P.parseNumeric(e.value) ?? 1)}
          options={options}
        >
          {props.pageSizeOptions.map((p) => (
            <option key={p} value={p}>
              {p} / page
            </option>
          ))}
        </Select>
      </ChakraV3.HStack>
    </ChakraV3.HStack>
  );
};
